/**
 * GPSLocator.jsx
 * ==============
 * Live GPS tracking using browser Geolocation watchPosition().
 * Marker moves on the map as the user walks through the field.
 * Re-classifies land every time user moves more than RECLASSIFY_DIST metres.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const RECLASSIFY_DIST = 30; // re-classify every 30 m of movement

const CROP_COLORS = {
  Paddy:'#00FF00', Sugarcane:'#FFD700', Banana:'#FF6B35',
  Coconut:'#228B22', Groundnut:'#DEB887', Fallow:'#8B4513',
};
const CROP_ICONS = {
  Paddy:'🌾', Sugarcane:'🎋', Banana:'🍌',
  Coconut:'🥥', Groundnut:'🥜', Fallow:'🟫',
};

/** Haversine distance in metres between two lat/lon points */
function distMetres(a, b) {
  const R = 6_371_000;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLon = (b.lon - a.lon) * Math.PI / 180;
  const s = Math.sin(dLat/2)**2 +
            Math.cos(a.lat*Math.PI/180) * Math.cos(b.lat*Math.PI/180) *
            Math.sin(dLon/2)**2;
  return R * 2 * Math.asin(Math.sqrt(s));
}

function accLabel(m) {
  if (m <= 5)   return { text:'Excellent (GPS chip)', color:'#4ade80' };
  if (m <= 20)  return { text:'Good (GPS)',            color:'#86efac' };
  if (m <= 100) return { text:'Fair (WiFi)',            color:'#fbbf24' };
  return               { text:'Low (Network)',          color:'#f87171' };
}

export default function GPSLocator({ onLocationDetected, onResult, onTrailUpdate, onTrackingStart }) {
  const [tracking,  setTracking]  = useState(false);
  const [position,  setPosition]  = useState(null);   // {lat,lon,alt,accuracy}
  const [result,    setResult]    = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [error,     setError]     = useState(null);
  const [trail,     setTrail]     = useState([]);      // [{lat,lon}]
  const [totalDist, setTotalDist] = useState(0);       // metres walked
  const [classCount,setClassCount]= useState(0);

  const watchIdRef    = useRef(null);
  const lastClassRef  = useRef(null);  // position of last classification
  const classifyingRef= useRef(false);

  // ── Start live tracking ─────────────────────────────────────────────────
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported by this browser.');
      return;
    }
    setError(null);
    setTrail([]);
    setTotalDist(0);
    setClassCount(0);
    setTracking(true);
    onTrackingStart && onTrackingStart();

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lon,
                altitude: alt, accuracy } = pos.coords;
        const loc = { lat, lon, alt: alt ?? 0, accuracy };

        setPosition(loc);

        // Update trail
        setTrail(prev => {
          const newTrail = [...prev, { lat, lon }];
          // Accumulate distance
          if (prev.length > 0) {
            const d = distMetres(prev[prev.length - 1], { lat, lon });
            setTotalDist(td => td + d);
          }
          onTrailUpdate && onTrailUpdate(newTrail);
          return newTrail;
        });

        // Fly map marker to new position
        onLocationDetected && onLocationDetected(loc);

        // Re-classify if moved RECLASSIFY_DIST metres from last classification
        const shouldClassify =
          !classifyingRef.current && (
            !lastClassRef.current ||
            distMetres(lastClassRef.current, { lat, lon }) >= RECLASSIFY_DIST
          );

        if (shouldClassify) {
          lastClassRef.current  = { lat, lon };
          classifyingRef.current = true;
          setClassifying(true);

          fetch(`${API_BASE}/api/classify-gps`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ lat, lon }),
          })
            .then(r => r.json())
            .then(data => {
              setResult(data);
              setClassCount(c => c + 1);
              onResult && onResult({ ...data, lat, lon });
            })
            .catch(() => {
              const demo = demoResult(lat, lon);
              setResult(demo);
              setClassCount(c => c + 1);
              onResult && onResult({ ...demo, lat, lon });
            })
            .finally(() => {
              classifyingRef.current = false;
              setClassifying(false);
            });
        }
      },
      (err) => {
        const msgs = {
          1: 'Location access denied — allow in browser settings.',
          2: 'Position unavailable — go outdoors.',
          3: 'GPS timeout — try again.',
        };
        setError(msgs[err.code] || err.message);
        setTracking(false);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 1000 }
    );
  }, [onLocationDetected, onResult, onTrailUpdate, onTrackingStart]);

  // ── Stop tracking ───────────────────────────────────────────────────────
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setTracking(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => stopTracking(), [stopTracking]);

  // ── Render ──────────────────────────────────────────────────────────────
  const cropColor = CROP_COLORS[result?.crop_type] || '#3b82f6';
  const acc       = position?.accuracy;
  const accInfo   = acc ? accLabel(acc) : null;
  const nextIn    = position && lastClassRef.current
    ? Math.max(0, RECLASSIFY_DIST - distMetres(lastClassRef.current, position)).toFixed(0)
    : RECLASSIFY_DIST;

  return (
    <div className="space-y-3">
      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
        📍 Live GPS Tracker
      </div>

      {/* ── Start / Stop button ──────────────────────────────────────── */}
      {!tracking ? (
        <button
          onClick={startTracking}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg
                     font-semibold text-sm transition-all active:scale-95"
          style={{
            background: 'linear-gradient(135deg,#166534,#16a34a)',
            color: '#fff',
            boxShadow: '0 4px 15px rgba(22,163,74,0.4)',
          }}
        >
          <span className="text-lg">📍</span>
          Start Live Tracking
        </button>
      ) : (
        <button
          onClick={stopTracking}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg
                     font-semibold text-sm transition-all active:scale-95"
          style={{ background: 'rgba(239,68,68,0.15)', border:'1px solid #ef4444', color:'#f87171' }}
        >
          <span className="w-2 h-2 rounded-full bg-red-500 pulse-dot inline-block" />
          Stop Tracking
        </button>
      )}

      {/* ── Error ────────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-900/30 border border-red-700/40 rounded-lg px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}

      {/* ── Live position card ───────────────────────────────────────── */}
      {position && (
        <div className="glass rounded-lg p-3 space-y-2">
          {/* Tracking indicator */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${tracking ? 'bg-red-500 pulse-dot' : 'bg-slate-500'}`} />
              <span className="text-xs font-semibold" style={{ color: tracking ? '#f87171' : '#64748b' }}>
                {tracking ? 'TRACKING LIVE' : 'STOPPED'}
              </span>
            </div>
            {accInfo && (
              <span className="text-xs" style={{ color: accInfo.color }}>
                ±{Math.round(acc)} m
              </span>
            )}
          </div>

          {/* Coordinates */}
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-slate-400">Latitude</span>
              <span className="font-mono text-green-400">{position.lat.toFixed(6)}°</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Longitude</span>
              <span className="font-mono text-green-400">{position.lon.toFixed(6)}°</span>
            </div>
            {position.alt > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-400">Altitude</span>
                <span className="text-slate-200">{position.alt.toFixed(1)} m</span>
              </div>
            )}
          </div>

          {/* Stats row */}
          <div className="flex gap-2 pt-1 border-t border-slate-700/50">
            <StatBadge label="Distance" value={`${totalDist >= 1000 ? (totalDist/1000).toFixed(2)+'km' : Math.round(totalDist)+'m'}`} />
            <StatBadge label="Points"   value={trail.length} />
            <StatBadge label="Scans"    value={classCount} />
          </div>

          {/* Next classification distance */}
          {tracking && (
            <div className="text-xs text-slate-500 text-center">
              {classifying
                ? <span className="text-green-400 animate-pulse">🔍 Classifying land…</span>
                : `Next scan in ~${nextIn} m`}
            </div>
          )}
        </div>
      )}

      {/* ── Classification result ─────────────────────────────────────── */}
      {result && (
        <div
          className="rounded-lg p-3 border fade-in space-y-2"
          style={{ borderColor: cropColor+'50', background: cropColor+'0d' }}
        >
          <div className="text-xs text-slate-400 font-medium">
            🌾 Land Classification #{classCount}
          </div>

          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center
                         text-2xl flex-shrink-0 border-2"
              style={{ borderColor: cropColor, background: cropColor+'20' }}
            >
              {CROP_ICONS[result.crop_type] || '🌱'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base leading-tight" style={{ color: cropColor }}>
                {result.crop_type}
              </div>
              <div className="text-xs text-slate-400">{result.growth_stage} Stage</div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-lg font-bold"
                style={{ color: result.confidence >= 0.8 ? '#4ade80' : '#fbbf24' }}>
                {Math.round(result.confidence * 100)}%
              </div>
              <div className="text-xs text-slate-500">conf.</div>
            </div>
          </div>

          <div className="space-y-1 text-xs">
            <Row label="NDVI"
              value={result.ndvi?.toFixed(4) ?? '—'}
              color={result.ndvi > 0.5 ? '#4ade80' : result.ndvi > 0.2 ? '#fbbf24' : '#f87171'}
            />
            <Row label="Stage confidence"
              value={`${Math.round((result.stage_confidence||0)*100)}%`} />
            <Row label="Tile source" value={result.tile_source ?? '—'} />
          </div>

          {/* NDVI bar */}
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full rounded-full"
              style={{
                width:`${Math.min((result.ndvi||0)*100,100)}%`,
                background:'linear-gradient(90deg,#ef4444,#fbbf24,#22c55e)',
              }} />
          </div>

        </div>
      )}

      {/* ── Tips (idle only) ─────────────────────────────────────────── */}
      {!tracking && !position && (
        <div className="space-y-1.5 pt-1">
          <div className="text-xs text-slate-500 font-medium">Tips:</div>
          {[
            ['📱','Open on smartphone for real GPS'],
            ['🌤','Go outdoors — GPS needs clear sky'],
            ['🚶','Walk through fields — marker follows you'],
            ['🔄',`Land re-scanned every ${RECLASSIFY_DIST} m moved`],
          ].map(([icon, text]) => (
            <div key={text} className="flex gap-2 text-xs text-slate-500">
              <span>{icon}</span><span>{text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-400">{label}</span>
      <span style={{ color: color || '#e2e8f0' }} className="font-medium">{value}</span>
    </div>
  );
}
function StatBadge({ label, value }) {
  return (
    <div className="flex-1 text-center bg-slate-800/60 rounded px-1 py-1">
      <div className="text-xs font-bold text-white">{value}</div>
      <div className="text-xs text-slate-500 leading-tight">{label}</div>
    </div>
  );
}

function demoResult(lat, lon) {
  const h = Math.abs(Math.round(lat * 10000 + lon * 10000)) % 1000;
  const crops  = ['Paddy','Sugarcane','Banana','Coconut','Groundnut','Fallow'];
  const stages = ['Early','Intermediate','Mature','Harvested'];
  const crop   = (lat>10.6&&lat<10.9&&lon>79.0&&lon<79.7)
    ? (h%3===0?'Sugarcane':h%5===0?'Coconut':'Paddy')
    : crops[h % crops.length];
  return {
    crop_type:        crop,
    confidence:       +(0.65+(h%25)/100).toFixed(4),
    growth_stage:     stages[h%stages.length],
    stage_confidence: +(0.60+(h%30)/100).toFixed(4),
    ndvi:             +(0.20+(h%65)/100).toFixed(4),
    tile_source:      'synthetic',
    demo_mode:        true,
  };
}
